/** メンバープロフィール編集コンポーネント */
<template>
  <div class="member-profile-editor">
    <ifield-b-modal
      id="the-member-profile-editor"
      ref="the-member-profile-editor-modal"
      v-bind:title="$t('theMemberProfileEditor.title')"
      centered
      scrollable
      no-body-border
      size="lg"
      ok-variant="success"
      ignore-enforce-focus-selector=".popover"
      v-bind:ok-title="$t('theMemberProfileEditor.action.save')"
      v-bind:cancel-title="$t('theMemberProfileEditor.action.cancel')"
      v-on:ok.prevent="$_saveUser"
    >
      <loading-mask isFullScreen v-bind:isLoading="isSaving" />
      <form-group id="member-profile-form" ref="form-group" />
    </ifield-b-modal>
    <loading-mask isFullScreen v-bind:isLoading="isLoading" />
  </div>
</template>

<script>
import { ATTRIBUTE_TYPE } from 'ifield-core-components'
import { RestApiClient } from 'ifield-core-components'
import _ from 'lodash'
import moment from 'moment'
import { toServerTz } from '@/js/TimezoneHelper'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

// フォーム用日時フォーマット
const DATETIME_FORMAT_FORM = 'YYYY-MM-DD HH:mm'
// ユーザー属性用複数項目セパレータ
const ATTRIBUTE_VALUES_SEPARATOR = '|'

export default {
  name: 'TheMemberProfileEditor',
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 編集対象のユーザー
       * @type {Object}
       */
      user: null,
      /**
       * ユーザー属性
       * @type {Array.<Object>}
       */
      attributes: null,
      // /** プロフィール画像の情報(添付ファイルコンポーネントに設定する) */
      // profileImage: {
      //   id: null,
      //   attach_file_type: null,
      //   filename_extension: null,
      // },
      /** 保存中の場合はtrue */
      isSaving: false,
      /** ロード中の場合はtrue */
      isLoading: false,
    }
  },
  computed: {
    /**
     * フォームの標準項目
     * @type {Array.<Object>}
     */
    formsStandard: function () {
      return [
        // ユーザー名
        {
          fieldName: 'user_name',
          label: this.$t('theMemberProfileEditor.field.userName'),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: (this.user && this.user.user_name) || null,
          validateRule: {
            required: true,
            max: 32,
          },
          placeholder: this.$t('theMemberProfileEditor.example.userName'),
        },
        // ユーザー名カナ
        {
          fieldName: 'user_name_kana',
          label: this.$t('theMemberProfileEditor.field.userNameKana'),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: (this.user && this.user.user_name_kana) || null,
          validateRule: {
            max: 64,
          },
          placeholder: this.$t('theMemberProfileEditor.example.userNameKana'),
        },
        // 電話番号
        {
          fieldName: 'tel',
          label: this.$t('theMemberProfileEditor.field.tel'),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: (this.user && this.user.tel) || null,
          validateRule: {
            regex: /^(0[0-9]{1,3}(-)?)?[0-9]{2,4}(-)?[0-9]{4}$/,
            numeric_dash: true,
            max: 15,
          },
          placeholder: this.$t('theMemberProfileEditor.example.tel'),
        },
        // メールアドレス
        {
          fieldName: 'email',
          label: this.$t('theMemberProfileEditor.field.email'),
          attributeType: ATTRIBUTE_TYPE.STRING,
          value: (this.user && this.user.email) || null,
          validateRule: {
            email: true,
            max: 128,
          },
          placeholder: this.$t('theMemberProfileEditor.example.email'),
        },
        // 備考
        {
          fieldName: 'remarks',
          label: this.$t('theMemberProfileEditor.field.remarks'),
          attributeType: ATTRIBUTE_TYPE.TEXT,
          value: (this.user && this.user.remarks) || null,
          placeholder: this.$t('theMemberProfileEditor.example.email'),
        },
      ]
    },
    /**
     * フォームのカスタム項目
     * @type {Array.<Object>}
     */
    formsAttribute: function () {
      if (!this.attributes || this.attributes.length === 0) {
        return []
      }

      const sortedAttributes = _.sortBy(this.attributes, [
        (item) => item.sort_order,
      ])
      return sortedAttributes.map((attr) => {
        const field = {
          fieldName: `${attr.id}`,
          label: attr.attribute_name,
          attributeType:
            // 文字列属性の複数行表示対応のため attribute_type を読み替える
            // DBのcode_settingの値変更は影響範囲が広く不可なので、フロント側で読み替えている
            attr.attribute_type == ATTRIBUTE_TYPE.STRING
              ? ATTRIBUTE_TYPE.TEXT
              : +attr.attribute_type,
          value: this.$_getAttributeValueFromData(attr.id),
        }

        let selectValues = null
        if (+attr.attribute_type === ATTRIBUTE_TYPE.SINGLE_SELECT) {
          if (attr.select_value) {
            selectValues = attr.select_value
              .split(ATTRIBUTE_VALUES_SEPARATOR)
              .map((v) => ({ value: v, text: v }))
          }
          field.selectValues = [{ value: null, text: '' }].concat(
            selectValues || []
          )
        } else if (+attr.attribute_type === ATTRIBUTE_TYPE.CHECK) {
          if (attr.select_value) {
            field.selectValues = attr.select_value
              .split(ATTRIBUTE_VALUES_SEPARATOR)
              .map((v) => ({ value: v, text: v }))
          }
        } else if (+attr.attribute_type === ATTRIBUTE_TYPE.TOGGLE) {
          if (attr.select_value) {
            const labels = attr.select_value.split(ATTRIBUTE_VALUES_SEPARATOR)
            field.selectValues = [
              { value: true, text: labels[0] },
              { value: false, text: labels[1] },
            ]
          } else {
            // トグルの場合selectValues未指定はフォームが表示されてしまうので属性値の場合はわざとエラーにする
            field.selectValues = []
          }
        }

        return field
      })
    },
    /**
     * カスタム項目のフィールド名
     * @type {Array.<String>}
     */
    attributeFieldNames: function () {
      return this.formsAttribute.map((f) => f.fieldName)
    },
    /**
     * フォームの添付ファイル項目
     * @type {Array.<Object>}
     */
    formsAttachFile: function () {
      return [
        {
          fieldName: 'profile_image',
          label: this.$t('theMemberProfileEditor.field.profileImage'),
          attributeType: ATTRIBUTE_TYPE.FILE,
          value: this.user ? { attachFiles: [this.user.profile_image] } : null,
          multiple: false,
          accept: 'image/png,image/jpeg,image/gif',
          acceptLabel: 'png, jpg, gif',
          recordId: (this.user && this.user.id) || null,
        },
      ]
    },
    /**
     * 結合したフォーム
     * @type {Array.<Object>}
     */
    forms: function () {
      return [
        ...this.formsStandard,
        ...this.formsAttribute,
        ...this.formsAttachFile,
      ]
    },
  },
  mounted: function () {
    this.$log.info('memberProfileEditor mounted!')
  },
  methods: {
    /**
     * メンバープロフィール編集モーダルを表示する
     * @public
     */
    showModal: async function (user) {
      this.$log.info('memberProfileEditor showModal', user.user_name)
      if (!user || user.id !== this.$store.getters.loginState.userId) {
        this.$log.error('cannot open the modal window. (invalid user)', user)
        return
      }
      this.user = user

      // ローディング表示
      this.$_showLoading()

      try {
        await this.$_getUserAttributes()
      } catch {
        this.$_hideLoading()
        return
      }

      this.$bvModal.show('the-member-profile-editor')
      this.$nextTick().then(() => {
        this.$_setData()
      })

      // ローディング終了
      this.$_hideLoading()
    },
    /**
     * メンバープロフィール編集モーダルを閉じる
     * @public
     */
    hideModal: function () {
      this.$bvModal.hide('the-member-profile-editor')
    },

    /**
     * ユーザー属性を取得する
     */
    $_getUserAttributes: async function () {
      const url = RestApiClient.createApiUrl('user_attribute')
      try {
        const json = await RestApiClient.executeGetApi(url)
        this.attributes = json.data
      } catch (error) {
        this.handleApiError(error)
        throw error
      }
    },

    /**
     * dataから指定プレイス属性IDの値を取得する
     * @param {Number} id 属性ID
     * @returns {*} 値
     */
    $_getAttributeValueFromData: function (id) {
      const matchedAttribute = this.user.attributes.find(
        (attr) => attr.attribute_id === id
      )
      if (!matchedAttribute) {
        return null
      }

      return this.$_convertAttributeValueToForm(matchedAttribute)
    },

    /**
     * プレイス属性の値をフォーム用に変換する
     * @param {Object} attribute 属性データ
     * @returns {*} 値
     */
    $_convertAttributeValueToForm: function (attribute) {
      const type = attribute.attribute_type
      const value = attribute.attribute_value

      if (value == null) {
        return value
      } else if (type == ATTRIBUTE_TYPE.DATETIME) {
        return moment(value).format(DATETIME_FORMAT_FORM)
      } else if (type == ATTRIBUTE_TYPE.TOGGLE) {
        const baseAttribute = this.attributes.find(
          (attr) => attr.id === attribute.attribute_id
        )
        const selectValues = baseAttribute.select_value
          ? baseAttribute.select_value.split(ATTRIBUTE_VALUES_SEPARATOR)
          : []
        return !selectValues.indexOf(value)
      } else if (value.indexOf(ATTRIBUTE_VALUES_SEPARATOR) > 0) {
        return value.split(ATTRIBUTE_VALUES_SEPARATOR)
      } else {
        return value
      }
    },

    /**
     * フォームに設定する
     */
    $_setData: function () {
      const form = this.$refs['form-group']
      form && form.setData(this.forms)
    },

    /**
     * ユーザー情報を保存する
     */
    $_saveUser: async function () {
      // フォームグループの値を取得する
      let formGroupValues
      try {
        formGroupValues = this.$refs['form-group'].getValues()
      } catch (error) {
        this.$log.error(error)
        return
      }

      this.$_showLoading()
      const requestBody = this.$_convertValuesToRequestBody(formGroupValues)

      // APIを実行する
      const url = RestApiClient.createApiUrl(`user/myaccount`)
      try {
        // 更新する
        await RestApiClient.executePutApi(url, requestBody)

        this.$emit('member-profile-edit-end')

        // storeを更新する
        this.$store.dispatch('updateLoginUserName', requestBody['user_name'])

        // モーダルを閉じる
        this.hideModal()
      } catch (error) {
        this.handleApiError(error)
      } finally {
        this.$_hideLoading()
      }
    },

    /**
     * フォームから取得した値をリクエストのbodyに変換する
     * @param {Array.<Object>} values フォームの値
     * @returns {Object} リクエストボディ
     */
    $_convertValuesToRequestBody: function (values) {
      const valueGroup = _.groupBy(values, (item) =>
        this.attributeFieldNames.includes(item.name) ? 'attribute' : 'standard'
      )

      const body = {}
      // 標準項目分
      this.$_applyStandardValuesToRequestBody(body, valueGroup.standard)
      // プレイス属性分
      this.$_applyAttributeValuesToRequestBody(body, valueGroup.attribute)
      // 所属グループは編集不可であるが、APIの仕様上groupsを送らないとnullに設定されてしまうため、グループの場合はorganization_idの配列
      body.groups = this.user.groups.map((value) => ({
        organization_id: value.id,
      }))

      return body
    },

    /**
     * 標準項目分の入力値をリクエストのbodyに反映する
     * @param {Object} body リクエストボディ
     * @param {Array.<Object>} values フォーム入力値
     */
    $_applyStandardValuesToRequestBody: function (body, values) {
      values.forEach((item) => {
        if (item.name === 'profile_image') {
          const { attachFiles, tempFiles } = item.value || {}
          if (attachFiles && attachFiles.length) {
            body.profile_image = attachFiles[0]
          } else if (tempFiles && tempFiles.length) {
            body.temp_profile_image = {
              temp_file_path: tempFiles[0].tempFilePath,
            }
          }
        } else {
          body[item.name] = item.value
        }
      })
    },

    /**
     * ユーザー属性分の入力値をリクエストのbodyに反映する
     * @param {Object} body リクエストボディ
     * @param {Array.<Object>} values フォーム入力値
     */
    $_applyAttributeValuesToRequestBody: function (body, values) {
      if (!Array.isArray(values) || values.length === 0) {
        return
      }

      const attributes = values.map((item) => {
        const baseAttribute = this.attributes.find(
          (attr) => attr.id === +item.name
        )
        const type = baseAttribute.attribute_type

        // トグル属性がnullのときfalseを代入
        if (type == ATTRIBUTE_TYPE.TOGGLE && item.value == null) {
          item.value = false
        }

        let value
        if (item.value === null) {
          value = item.value
        } else if (type == ATTRIBUTE_TYPE.DATETIME) {
          value = toServerTz(item.value)
        } else if (type == ATTRIBUTE_TYPE.TOGGLE) {
          const selectValues = baseAttribute.select_value.split(
            ATTRIBUTE_VALUES_SEPARATOR
          )
          value = selectValues[+!item.value]
        } else if (Array.isArray(item.value)) {
          value = item.value.join(ATTRIBUTE_VALUES_SEPARATOR)
        } else {
          value = item.value
        }

        return {
          attribute_id: +item.name,
          attribute_value: value,
        }
      })

      body.attributes = attributes
    },

    /**
     * 初期表示時のローディングを表示する
     */
    $_showInitialLoading: function () {
      this.isLoading = true
    },

    /**
     * 初期表示時のローディングを非表示にする
     */
    $_hideInitialLoading: function () {
      this.isLoading = false
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isSaving = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isSaving = false
    },
  },
}
</script>
